import { useEffect, useState } from "react";
import { Autocomplete, TextField, Typography, createFilterOptions } from "@mui/material";
import { formatValue } from "../../utils/formatValue";
import {
  listarEmpresasTabelaNcm,
  listarCnpjsCadastradosTabelaNcm,
} from "../../services/api-planejamento-tributario.service";

/**
 * CNPJs (com razão social e UF) do grupo econômico selecionado. `idGrupo` vazio = lista
 * vazia, sem chamada. Troca de grupo no meio do carregamento descarta a resposta antiga
 * (`ativo`), senão a lista do grupo anterior podia sobrescrever a do atual.
 */
export function useEmpresasDoGrupo(idGrupo) {
  const [empresas, setEmpresas] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");

  useEffect(() => {
    if (!idGrupo) {
      setEmpresas([]);
      setErro("");
      return undefined;
    }
    let ativo = true;
    setCarregando(true);
    setErro("");
    listarEmpresasTabelaNcm(idGrupo)
      .then((res) => {
        if (ativo) setEmpresas(res?.data || []);
      })
      .catch(() => {
        if (ativo) {
          setEmpresas([]);
          setErro("Não foi possível carregar os CNPJs do cliente.");
        }
      })
      .finally(() => {
        if (ativo) setCarregando(false);
      });
    return () => {
      ativo = false;
    };
  }, [idGrupo]);

  return { empresas, carregando, erro };
}

/**
 * CNPJs que já têm item na Tabela NCM — fonte do FILTRO da listagem, de propósito separada de
 * useEmpresasDoGrupo (fonte do CADASTRO nos modais, que precisa oferecer também as filiais sem
 * nenhum item ainda). `idGrupo` vazio = todos os grupos; `habilitado` false = lista vazia sem
 * chamada (filtro "Todas as Empresas", onde CNPJ não se aplica). `versao` muda depois de salvar
 * ou cadastrar, pra um CNPJ que acabou de ganhar o primeiro item aparecer no filtro.
 */
export function useCnpjsCadastrados(idGrupo, habilitado, versao) {
  const [cnpjs, setCnpjs] = useState([]);

  useEffect(() => {
    if (!habilitado) {
      setCnpjs([]);
      return undefined;
    }
    let ativo = true;
    listarCnpjsCadastradosTabelaNcm(idGrupo)
      .then((res) => {
        if (ativo) setCnpjs(res?.data || []);
      })
      .catch((e) => {
        // Não trava a tela: sem a lista, o filtro só fica com "Todos"/"Sem CNPJ" — mas registra.
        console.warn("Tabela NCM: falha ao carregar os CNPJs do filtro", e);
        if (ativo) setCnpjs([]);
      });
    return () => {
      ativo = false;
    };
  }, [idGrupo, habilitado, versao]);

  return { cnpjs };
}

export const formatarCnpj = (cnpj) => (cnpj ? formatValue("cnpj", cnpj) : "");

/** Busca aceita CNPJ só com dígitos, formatado, ou pedaço da razão social. */
const filtrarEmpresas = createFilterOptions({
  stringify: (e) => `${e.cnpj} ${formatarCnpj(e.cnpj)} ${e.razao_social ?? ""}`,
});

/**
 * Seletor de CNPJ do cliente — mesmo padrão de dois níveis da "Nova apuração" (Grupo →
 * Empresa), mas com o CNPJ aparecendo UMA vez por opção (formatado), e razão social + UF
 * embaixo. `value` é o CNPJ (14 dígitos) e `onChange` recebe a empresa inteira (ou null),
 * porque quem chama precisa da UF pra preencher o Estado.
 */
export function CampoCnpj({ empresas, carregando, erroCarga, value, onChange, disabled, hint }) {
  const selecionada = empresas.find((e) => e.cnpj === value) || null;
  return (
    <>
      <Autocomplete
        size="small"
        options={empresas}
        value={selecionada}
        onChange={(_, empresa) => onChange(empresa)}
        getOptionLabel={(e) => formatarCnpj(e?.cnpj)}
        isOptionEqualToValue={(a, b) => a?.cnpj === b?.cnpj}
        filterOptions={filtrarEmpresas}
        loading={carregando}
        disabled={disabled}
        noOptionsText={carregando ? "Carregando CNPJs…" : "Nenhuma empresa ativa neste cliente."}
        renderOption={(props, e) => (
          <li {...props} key={e.cnpj}>
            <div>
              <Typography variant="body2">{formatarCnpj(e.cnpj)}</Typography>
              <Typography variant="caption" color="text.secondary">
                {[e.razao_social, e.uf || "sem UF cadastrada"].filter(Boolean).join(" · ")}
              </Typography>
            </div>
          </li>
        )}
        renderInput={(params) => (
          <TextField {...params} label="CNPJ" placeholder="Selecione o CNPJ…" error={Boolean(erroCarga)} />
        )}
      />
      {(erroCarga || hint) && (
        <Typography
          component="span"
          sx={{ fontSize: 12, color: erroCarga ? "#c62828" : "#888", marginTop: "6px", display: "block" }}
        >
          {erroCarga || hint}
        </Typography>
      )}
    </>
  );
}
